import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EligibilityType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { InsuranceStatus } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { isEmpty } from 'lodash-es';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { IntegrationsQueries } from '@frontend/api-integrations';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { useAppScopeStore } from '@frontend/scope';
import { WivPromotionCard, useWivTeaserHandler } from '@frontend/self-upgrade';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Heading, SpinningLoader, styles, useModalControl } from '@frontend/design-system';
import {
  ActionsMenu,
  ErrorBanner,
  InsuranceDetailList,
  VerificationMetricSidePanel,
  VerifyInsuranceSidePanel,
  ToggleExpandContainer,
} from './components';
import { AutoVerificationSuccessBanner } from './components/AutoVerificationSuccessBanner';
import { ManualVerificationSuccessBanner } from './components/ManualVerificationSuccessBanner';
import { VerifiedInsuranceDetailsList } from './components/verified-insurance-details-list';
import { DateRangeOptionValue, DefaultInsuranceDetailsValues } from './constants';
import { prepareInsuranceDetails, prepareInsuranceDetailsForNonDental } from './helpers';
import { useGetLocationVerticalForPayerList } from './hooks/use-get-location-vertical-for-payer-list';
import { useGetVerifiedInsuranceDetailsShallowStore } from './hooks/use-get-verified-insurance-details-store';
import { InsuranceDetailsContainerProps } from './insurance-details-container';
import { VerifyInsuranceButton } from './insurance-status-chip-button';
import { PersonInfoStoreProvider, usePersonInfoStore } from './providers';
import { DropdownOption, InsuranceDetails as InsuranceDetailsType } from './types';

export const InsuranceDetailsComponent = () => {
  const { t } = useTranslation('insuranceDetails');
  const ref = useRef<HTMLDivElement>(null);
  const [defaultDateRange, setDefaultDateRange] = useState<DateRangeOptionValue>('today');
  const { accessibleLocationData } = useAppScopeStore();
  const verifyInsuranceModalControl = useModalControl();
  const verificationMetricModalControl = useModalControl();
  const { personInfo, locationId } = usePersonInfoStore();
  const personId = personInfo?.PersonID as string;

  const { locationVerticalForPayerList, isDental, isWellnessVertical } = useGetLocationVerticalForPayerList(
    locationId || ''
  );

  const { locationIdWiseCustomizationFlagDetails } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();

  const isEnhancedInsuranceVerificationEnabled = locationIdWiseCustomizationFlagDetails?.[locationId!] || false;

  const { setVerifiedInsuranceDetails } = useGetVerifiedInsuranceDetailsShallowStore('setVerifiedInsuranceDetails');

  const locationData = accessibleLocationData[locationId!];
  const isDemoOrDevOffice = locationData?.opsType === 'DEV' || locationData?.opsType === 'DEMO';

  const { isLoading: isLoadingWivEligibility, showWeaveVerify } = useWivTeaserHandler({ locationIds: [locationId!] });

  const { isInsuranceDetailsWritebackSupported, isLoading: isLoadingWritebackSupportedDetails } =
    IntegrationsQueries.useIsWritebackSupported({
      locationId: locationId!,
      sourceId: personInfo?.SourceID || '',
    });

  const { data: insuranceDetails, isLoading: isLoadingInsuranceDetails } =
    InsuranceDetailQueries.useGetInsuranceDetailsByPersonId({
      personId,
      locationId: locationId || '',
      options: {
        enabled: !!personId && !!locationId && isDental,
      },
    });

  const hasCoverageDetails = !!insuranceDetails?.coverage?.thirdPartyCoverage?.coverage;

  const { data: verificationDetails, isLoading: isLoadingVerificationDetails } =
    InsuranceDetailQueries.useGetDentalInsuranceVerificationDetailsPersonId({
      personId,
      locationId: locationId || '',
      options: {
        enabled: isDental && hasCoverageDetails && showWeaveVerify,
      },
    });

  const { data: nonDentalInsuranceCoverageInfo, isLoading: isLoadingNonDentalInsuranceVerificationDetails } =
    InsuranceDetailQueries.useGetNonDentalInsuranceVerificationDetailsByPersonId({
      personId,
      locationId: locationId || '',
      options: {
        enabled: !isDental && showWeaveVerify,
      },
    });

  const insuranceVerificationDetails = isDental ? verificationDetails : nonDentalInsuranceCoverageInfo;

  const requestId =
    !isDental && isWellnessVertical
      ? nonDentalInsuranceCoverageInfo?.coverage?.thirdPartyCoverage?.externalRequestId
      : '';

  const isAutoVerification = !!insuranceVerificationDetails?.coverage?.thirdPartyCoverage?.isAutoVerification;
  const hasErrors = !isEmpty(insuranceVerificationDetails?.coverage?.thirdPartyCoverage?.errorDetails);
  const hasCoverage = !isEmpty(insuranceVerificationDetails?.coverage?.thirdPartyCoverage?.coverage);

  const { data: payerListResponse } = InsuranceDetailQueries.useGetPayerList({
    locationId: locationId || '',
    vertical: locationVerticalForPayerList,
    eligibilityType: isEnhancedInsuranceVerificationEnabled ? EligibilityType.ENHANCED : EligibilityType.LITE,
    options: {
      enabled: !!locationId,
    },
  });
  const insuranceInfoForDental = useMemo(() => {
    if (insuranceDetails && isDental) {
      const insuranceDetailsData = { ...insuranceDetails };
      const payerList =
        payerListResponse?.map<DropdownOption>((item) => ({
          label: item.name,
          value: item.id,
        })) ?? [];

      if (verificationDetails?.coverage) {
        insuranceDetailsData.coverage = verificationDetails?.coverage || {};
      }

      return prepareInsuranceDetails(insuranceDetailsData, payerList);
    }
    return null;
  }, [insuranceDetails, isDental, payerListResponse, verificationDetails?.coverage]);

  const insuranceInfoForWellness = useMemo(() => {
    if (nonDentalInsuranceCoverageInfo && isWellnessVertical) {
      const payerList =
        payerListResponse?.map<DropdownOption>((item) => ({
          label: item.name,
          value: item.id,
        })) ?? [];

      if (nonDentalInsuranceCoverageInfo.coverage) {
        return prepareInsuranceDetailsForNonDental(nonDentalInsuranceCoverageInfo?.coverage || {}, payerList);
      }
    }
    return null;
  }, [nonDentalInsuranceCoverageInfo, isWellnessVertical, payerListResponse]);

  const insuranceInfo = isDental ? insuranceInfoForDental : insuranceInfoForWellness;

  const { scrollTo, setScrollTo } = useContactPanelShallowStore('scrollTo', 'setScrollTo');
  useEffect(() => {
    if (scrollTo === 'insurance-details') {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
      setScrollTo(undefined);
    }
  }, [scrollTo]);

  const handleReportLearnMoreClick = () => {
    setDefaultDateRange('this-month');
    verificationMetricModalControl.openModal();
  };
  const handleVerificationMetricActionMenuClick = () => {
    setDefaultDateRange('today');
    verificationMetricModalControl.openModal();
  };

  // Setting up the verified insurance details
  const insuranceInfoDetails = useMemo<InsuranceDetailsType>(() => {
    if (!insuranceInfo) {
      setVerifiedInsuranceDetails({ ...DefaultInsuranceDetailsValues, personId: '' });
      return DefaultInsuranceDetailsValues;
    }

    setVerifiedInsuranceDetails({ ...insuranceInfo, personId });
    return insuranceInfo;
  }, [insuranceInfo, personId]);

  // Cleanup function to reset the verified insurance details
  useEffect(() => {
    return () => {
      setVerifiedInsuranceDetails({ ...DefaultInsuranceDetailsValues, personId: '' });
    };
  }, []);

  const isLoading =
    isLoadingInsuranceDetails ||
    isLoadingWivEligibility ||
    isLoadingVerificationDetails ||
    isLoadingWritebackSupportedDetails ||
    isLoadingNonDentalInsuranceVerificationDetails;

  if (isLoading) {
    return (
      <div css={[styles.flexCenter, { padding: theme.spacing(2) }]}>
        <SpinningLoader size='small' />
      </div>
    );
  }

  if (!personId) return null;

  const hasActiveOrVerifiedInsurance =
    insuranceInfoDetails?.thirdPartyCoverageStatus.toUpperCase() === InsuranceStatus.ACTIVE ||
    insuranceInfoDetails?.thirdPartyCoverageStatus.toUpperCase() === InsuranceStatus.VERIFIED;

  const shouldShowVerifiedInsuranceDetails = hasActiveOrVerifiedInsurance;

  return (
    <>
      <section ref={ref} css={{ padding: theme.spacing(2, 2, 1) }}>
        <header css={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing(2) }}>
          <Heading level={3}>{t('Insurance')}</Heading>
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            {(hasErrors || !hasCoverage) && (
              <VerifyInsuranceButton
                showWeaveVerify={showWeaveVerify}
                onClick={verifyInsuranceModalControl.openModal}
              />
            )}
            <ActionsMenu
              verificationStatus={insuranceInfoDetails?.thirdPartyCoverageStatus}
              onVerifyInsuranceClick={verifyInsuranceModalControl.openModal}
              onVerificationMetricClick={handleVerificationMetricActionMenuClick}
              requestId={requestId}
            />
          </div>
        </header>
        <ErrorBanner
          isRetryButtonVisible
          errorDetails={insuranceDetails?.coverage?.thirdPartyCoverage?.errorDetails}
          onRetryClick={verifyInsuranceModalControl.openModal}
          isAutoVerification={isAutoVerification}
        />
        {hasCoverage && isAutoVerification && <AutoVerificationSuccessBanner css={{ margin: theme.spacing(2, 0) }} />}
        {hasCoverage && !isAutoVerification && (
          <ManualVerificationSuccessBanner
            isInsuranceDetailsWritebackSupported={isInsuranceDetailsWritebackSupported}
            css={{ margin: theme.spacing(2, 0) }}
            isDemoOffice={isDemoOrDevOffice}
          />
        )}

        <ToggleExpandContainer>
          {shouldShowVerifiedInsuranceDetails ? (
            <VerifiedInsuranceDetailsList locationId={locationId || ''} personId={personId} />
          ) : (
            <InsuranceDetailList
              insuranceDetails={insuranceInfoDetails}
              locationId={locationId || ''}
              verifyInsuranceModalControl={verifyInsuranceModalControl}
              personId={personId}
            />
          )}
        </ToggleExpandContainer>
      </section>
      <VerifyInsuranceSidePanel
        {...verifyInsuranceModalControl.modalProps}
        onReportLearnMoreClick={handleReportLearnMoreClick}
      />
      <VerificationMetricSidePanel {...verificationMetricModalControl.modalProps} dateRange={defaultDateRange} />
    </>
  );
};

export const InsuranceDetails = React.memo(({ person, locationId }: InsuranceDetailsContainerProps) => {
  const { t } = useTranslation('insuranceDetails');

  const {
    isLoading: isLoadingWivEligibility,
    showWeaveVerify,
    showWeaveVerifyPromotion,
  } = useWivTeaserHandler({ locationIds: [locationId] });

  if (!person || !locationId || (!showWeaveVerify && !showWeaveVerifyPromotion)) return null;

  if (isLoadingWivEligibility) {
    return (
      <div css={[styles.flexCenter, { padding: theme.spacing(2) }]}>
        <SpinningLoader size='small' />
      </div>
    );
  }

  if (showWeaveVerifyPromotion) {
    return (
      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1), padding: theme.spacing(2) }}>
        <Heading level={3}>{t('Insurance')}</Heading>
        <WivPromotionCard locationIds={[locationId]} source='contacts' />
      </div>
    );
  }

  return (
    <PersonInfoStoreProvider personInfo={person} locationId={locationId}>
      <InsuranceDetailsComponent />
    </PersonInfoStoreProvider>
  );
});

InsuranceDetails.displayName = 'InsuranceDetails';
